'use client';

import { clsx } from 'clsx';
import { PropsWithChildren, useRef, useState, useEffect } from 'react';

type ScrollGradientContainerProps = PropsWithChildren<{
  className?: string;
  maxHeight?: number | string;
  overflow?: string;
}>;

export const ScrollGradientContainer = ({
  children,
  className,
  maxHeight,
  overflow,
}: ScrollGradientContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isTopShadowVisible, setIsTopShadowVisible] = useState(false);
  const [isBottomShadowVisible, setIsBottomShadowVisible] = useState(false);

  const handleScroll = () => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;

    setIsTopShadowVisible(scrollTop > 0);
    setIsBottomShadowVisible(scrollTop + clientHeight < scrollHeight);
  };

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    handleScroll();

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className={clsx(
        'relative overflow-hidden',
        {
          'before:content-[""] before:absolute before:top-0 before:left-0 before:right-0 before:h-16 before:bg-gradient-to-b before:from-white before:to-white/0 before:pointer-events-none':
            isTopShadowVisible,
          'after:content-[""] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-t after:from-white after:to-white/0 after:pointer-events-none':
            isBottomShadowVisible,
        },
        className
      )}
      style={{ maxHeight }}
    >
      <div
        ref={containerRef}
        className='overflow-y-auto custom-scrollbar'
        style={{ maxHeight, overflow }}
      >
        {children}
      </div>
    </div>
  );
};
